import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingRecordDetailsCustomer } from '@navio/nsdk';
import { NoddiButton } from 'noddi-ui-web';
import { customerRoutes, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomerLoginModal } from '../Login/CustomerLoginModal';

interface ChangeBookingButtonProps {
  booking: BookingRecordDetailsCustomer;
}

export const ChangeBookingButton = ({ booking }: ChangeBookingButtonProps) => {
  const navigate = useNavigate();
  const [hasTriggeredChangeBooking, setHasTriggeredChangeBooking] = useState(false);
  const { isLoggedIn } = useAuthContext();

  if (booking.isCancelled) {
    return null;
  }

  const cutoffDisplay = booking.cancelAllowedUntil
    ? noddiFormatDate(booking.cancelAllowedUntil, NoddiDateFormats.DATE_TIME)
    : null;

  if (!booking.isCancelAllowed) {
    return (
      <p className='text-sm text-muted-foreground'>
        {cutoffDisplay
          ? t`The booking could be edited or cancelled until ${cutoffDisplay}.`
          : t`It is too late to make changes to this booking.`}
      </p>
    );
  }

  function handleChangeBooking() {
    if (isLoggedIn) {
      navigate(customerRoutes.changeBooking.getPath({ slug: booking.slug }));
      return;
    }

    setHasTriggeredChangeBooking(true);
  }

  return (
    <>
      <div className='flex flex-col gap-2'>
        <div className='md:flex md:justify-end'>
          <NoddiButton className='w-full md:w-auto' startIcon='Edit' onClick={handleChangeBooking}>
            <Trans>Edit booking</Trans>
          </NoddiButton>
        </div>
        {cutoffDisplay && (
          <p className='text-xs text-muted-foreground'>
            {t`The booking can be edited or cancelled until ${cutoffDisplay}.`}
          </p>
        )}
      </div>
      <CustomerLoginModal
        open={hasTriggeredChangeBooking}
        onClose={() => setHasTriggeredChangeBooking(false)}
        title={t`Sign in`}
        description={t`Please sign in order to make changes to your booking`}
        // oxlint-disable-next-line typescript/no-misused-promises -- async onLoginSuccess runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
        onLoginSuccess={() => navigate(customerRoutes.changeBooking.getPath({ slug: booking.slug }))}
      />
    </>
  );
};
